import { TextInput } from '@mantine/core'
import { FunctionComponent } from 'react'

/** Surrounding whitespace is forgiven — a phrase copied off the row or field behind carries some often enough. */
export const isPhraseTyped = (phrase: string, typed: string | undefined): boolean => (typed ?? '').trim() === phrase

type Props = {
  /** The exact words that let the action through: read back in the sentence, and expected in the field. */
  phrase: string
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  /** Already prefixed by the caller — this component is used from both modals and flyouts. */
  trackingLabel: string
}

/**
 * The words a one-way action has to be typed back before it is allowed through.
 *
 * The ask is a sentence rather than a field label, and the phrase inside it is bold: what has to be typed
 * is the one thing on the card that must not be guessed at, and a bare "Type the media name to confirm"
 * above an empty field leaves the reader to go and find the name themselves. Here it is on the card, so
 * confirming stays a matter of reading what is about to happen.
 *
 * No box around it. The callers all sit it at the end of a modal body that has just spelled out what is
 * about to happen, and a bordered panel there reads as a second, separate thing to deal with rather than
 * as the last step of the sentence above it. A gap and an italic line carry the same "now do this" without
 * cutting the dialog in two.
 *
 * The field is sized to a name rather than to the modal. A phrase is a handful of words, and an input
 * stretched the full width of a dialog reads as a place to write something — which is the opposite of
 * what this one wants, where the only correct value is the one printed directly above it.
 */
export const ConfirmationPhraseField: FunctionComponent<Props> = ({ phrase, value, onChange, disabled, trackingLabel }) => (
  <div className={'mt-4 flex flex-col gap-2'}>
    <p className={'italic'}>
      Type <strong>{phrase}</strong> to confirm.
    </p>
    <TextInput
      className={'max-w-xs'}
      aria-label={`Type ${phrase} to confirm`}
      placeholder={phrase}
      value={value}
      // Read off the event before the updater runs: React has recycled it by then, leaving no target to read.
      onChange={({ currentTarget: { value } }) => onChange(value)}
      disabled={disabled}
      autoComplete={'off'}
      data-tracking={trackingLabel}
    />
  </div>
)
